// Phase 111 — full-screen translucent command map overlay (M key).
// Shows top-down map with bases, flag positions, player dots, vehicles.
import { useMemo, type CSSProperties, type ReactElement } from 'react';

type Vec3Like = { x?: number; y?: number; z?: number };

type CommandPlayer = {
  id?: string;
  displayName?: string;
  team?: string | null;
  position?: Vec3Like;
  carryingFlagTeam?: string | null;
  health?: number;
};

type CommandFlag = {
  team?: string | null;
  position?: Vec3Like;
  homePosition?: Vec3Like;
  holderId?: string | null;
  state?: string | null;
};

type CommandVehicle = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  active?: boolean | null;
};

// Phase 152 — sensors render with their coverage ring; offline (generator
// dead or sensor destroyed) sensors are dimmed.
type CommandSensor = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  active?: boolean | null;
  health?: number | null;
};

// Phase 155 — waypoint ping passed back to parent which broadcasts via socket.
type CommandWaypoint = {
  id: string;
  team?: string | null;
  position?: Vec3Like;
  ttlSeconds?: number;
  color?: string | null;
  callerName?: string | null;
  kind?: string | null;
};

type CommandMapProps = {
  visible: boolean;
  localPlayer: CommandPlayer;
  remotePlayers: Record<string, CommandPlayer>;
  flags: CommandFlag[];
  vehicles?: CommandVehicle[];
  // Phase 155 — render team waypoints and emit clicks back to network layer.
  pingWaypoints?: CommandWaypoint[];
  onPing?: (worldX: number, worldZ: number) => void;
  // Phase 152 — base sensors with coverage ring; dimmed when offline.
  sensors?: CommandSensor[];
  // Phase 151 — friendly base sensor coverage radius (world units).
  sensorCoverageRadius?: number;
};

const MAP_RADIUS = 360; // world units shown per side
const MAP_PIXELS = 520;

function teamColor(team?: string | null): string {
  const t = team?.toLowerCase() ?? null;
  if (t === 'red' || t === 'blood_eagle') return '#ff3838';
  if (t === 'blue' || t === 'storm') return '#2dd4d4';
  return '#9db6c8';
}

function projectToMap(pos: Vec3Like | undefined, center: Vec3Like): { x: number; y: number } | null {
  if (!pos || pos.x == null || pos.z == null) return null;
  const cx = center.x ?? 0;
  const cz = center.z ?? 0;
  const dx = (pos.x - cx) / (MAP_RADIUS * 2);
  const dz = (pos.z - cz) / (MAP_RADIUS * 2);
  // Map y axis: world +Z is "down" on map by convention; clamp into render area.
  const px = MAP_PIXELS / 2 + dx * MAP_PIXELS;
  const py = MAP_PIXELS / 2 + dz * MAP_PIXELS;
  if (px < -20 || px > MAP_PIXELS + 20 || py < -20 || py > MAP_PIXELS + 20) return null;
  return { x: px, y: py };
}

export function CommandMap({
  visible,
  localPlayer,
  remotePlayers,
  flags,
  vehicles = [],
  pingWaypoints = [],
  onPing,
  sensors = [],
  sensorCoverageRadius = 150,
}: CommandMapProps) {
  const center = useMemo<Vec3Like>(() => ({ x: 0, y: 0, z: 0 }), []);
  const localTeam = (localPlayer.team ?? '').toLowerCase();

  if (!visible) return null;

  const containerStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,4,10,0.78)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    zIndex: 250,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
  };

  const panelStyle: CSSProperties = {
    width: MAP_PIXELS,
    height: MAP_PIXELS,
    position: 'relative',
    background:
      'radial-gradient(circle at 50% 50%, rgba(20,40,70,0.55), rgba(4,8,18,0.85))',
    border: '1px solid rgba(120,200,255,0.32)',
    boxShadow: '0 0 40px rgba(0,0,0,0.6), inset 0 0 60px rgba(45,212,212,0.05)',
    overflow: 'hidden',
  };

  // Grid lines
  const gridLines: ReactElement[] = [];
  for (let i = 1; i < 8; i++) {
    const p = (i / 8) * MAP_PIXELS;
    gridLines.push(
      <line key={`gh-${i}`} x1={0} y1={p} x2={MAP_PIXELS} y2={p} stroke="rgba(120,200,255,0.06)" strokeWidth={1} />,
    );
    gridLines.push(
      <line key={`gv-${i}`} x1={p} y1={0} x2={p} y2={MAP_PIXELS} stroke="rgba(120,200,255,0.06)" strokeWidth={1} />,
    );
  }

  const friendlies: CommandPlayer[] = [localPlayer, ...Object.values(remotePlayers).filter(
    (p) => (p.team ?? '').toLowerCase() === localTeam,
  )];

  // Phase 155 — click handler unprojects pixel coords back to world XZ and
  // forwards to the network layer through onPing.
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onPing) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    const cx = center.x ?? 0;
    const cz = center.z ?? 0;
    const worldX = cx + ((px - MAP_PIXELS / 2) / MAP_PIXELS) * (MAP_RADIUS * 2);
    const worldZ = cz + ((py - MAP_PIXELS / 2) / MAP_PIXELS) * (MAP_RADIUS * 2);
    onPing(worldX, worldZ);
  };

  return (
    <div style={containerStyle}>
      <div style={panelStyle} onClick={handleClick}>
        <svg width={MAP_PIXELS} height={MAP_PIXELS} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {gridLines}
          {/* Center crosshair */}
          <line x1={MAP_PIXELS / 2 - 10} y1={MAP_PIXELS / 2} x2={MAP_PIXELS / 2 + 10} y2={MAP_PIXELS / 2} stroke="rgba(255,255,255,0.18)" strokeWidth={1} />
          <line x1={MAP_PIXELS / 2} y1={MAP_PIXELS / 2 - 10} x2={MAP_PIXELS / 2} y2={MAP_PIXELS / 2 + 10} stroke="rgba(255,255,255,0.18)" strokeWidth={1} />

          {/* Flag home bases */}
          {flags.map((flag, idx) => {
            const home = projectToMap(flag.homePosition ?? flag.position, center);
            if (!home) return null;
            const color = teamColor(flag.team);
            return (
              <g key={`base-${idx}`}>
                <circle cx={home.x} cy={home.y} r={22} fill="none" stroke={color} strokeWidth={1.5} opacity={0.7} />
                <circle cx={home.x} cy={home.y} r={3} fill={color} />
              </g>
            );
          })}

          {/* Current flag positions (if not at home) */}
          {flags.map((flag, idx) => {
            const pos = projectToMap(flag.position, center);
            if (!pos) return null;
            const color = teamColor(flag.team);
            const taken = Boolean(flag.holderId);
            return (
              <g key={`flag-${idx}`}>
                <polygon
                  points={`${pos.x},${pos.y - 8} ${pos.x + 7},${pos.y + 4} ${pos.x - 7},${pos.y + 4}`}
                  fill={color}
                  stroke="#fff"
                  strokeWidth={taken ? 2 : 1}
                  opacity={taken ? 1 : 0.9}
                />
              </g>
            );
          })}

          {/* Vehicles */}
          {vehicles.filter((v) => v.active !== false).map((vehicle, idx) => {
            const pos = projectToMap(vehicle.position, center);
            if (!pos) return null;
            const color = teamColor(vehicle.team);
            return (
              <rect
                key={`veh-${idx}`}
                x={pos.x - 4}
                y={pos.y - 4}
                width={8}
                height={8}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
              />
            );
          })}

          {/* Phase 151/152 — base sensors with coverage rings (dimmed when offline) */}
          {sensors.map((sensor, idx) => {
            const pos = projectToMap(sensor.position, center);
            if (!pos) return null;
            const isOffline = sensor.active === false || (sensor.health ?? 0) <= 0;
            const baseColor = teamColor(sensor.team);
            const ringOpacity = isOffline ? 0.08 : 0.22;
            const dotOpacity = isOffline ? 0.3 : 1;
            const coverageRadiusPx = (sensorCoverageRadius / (MAP_RADIUS * 2)) * MAP_PIXELS;
            return (
              <g key={`sensor-${sensor.id ?? idx}`}>
                <circle cx={pos.x} cy={pos.y} r={coverageRadiusPx} fill={baseColor} opacity={ringOpacity * 0.4}
                        stroke={baseColor} strokeWidth={1} strokeOpacity={ringOpacity}
                        strokeDasharray={isOffline ? '4 4' : undefined} />
                <rect x={pos.x - 3} y={pos.y - 3} width={6} height={6} fill={baseColor} opacity={dotOpacity}
                      stroke={isOffline ? '#444' : '#fff'} strokeWidth={0.5} />
                {isOffline ? (
                  <text x={pos.x + 6} y={pos.y - 4} fontSize={8} fill="#888" fontFamily="monospace">OFF</text>
                ) : null}
              </g>
            );
          })}

          {/* Phase 155 — team waypoint pings: vertical pulse + label */}
          {pingWaypoints.map((wp) => {
            const pos = projectToMap(wp.position, center);
            if (!pos) return null;
            const color = wp.color ?? teamColor(wp.team);
            const isRepair = wp.kind === 'repair-needed';
            const ttl = Math.max(0, wp.ttlSeconds ?? 0);
            const opacity = Math.min(1, ttl / 4);
            return (
              <g key={`wp-${wp.id}`} opacity={opacity}>
                <circle cx={pos.x} cy={pos.y} r={14} fill="none" stroke={color} strokeWidth={1.5}
                        strokeDasharray={isRepair ? '3 3' : undefined}>
                  <animate attributeName="r" from="6" to="18" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from={String(opacity)} to="0" dur="1.2s" repeatCount="indefinite" />
                </circle>
                <circle cx={pos.x} cy={pos.y} r={3} fill={color} />
                {isRepair ? (
                  <text x={pos.x + 8} y={pos.y + 3} fontSize={9} fill={color} fontFamily="monospace">
                    REPAIR
                  </text>
                ) : null}
              </g>
            );
          })}

          {/* Friendly player dots */}
          {friendlies.map((player, idx) => {
            const pos = projectToMap(player.position, center);
            if (!pos) return null;
            const isLocal = player.id === localPlayer.id;
            const color = teamColor(player.team);
            return (
              <g key={`player-${player.id ?? idx}`}>
                <circle cx={pos.x} cy={pos.y} r={isLocal ? 5 : 4} fill={color} stroke="#fff" strokeWidth={isLocal ? 1.5 : 0.5} />
                {isLocal ? (
                  <circle cx={pos.x} cy={pos.y} r={10} fill="none" stroke={color} strokeWidth={1} opacity={0.5} />
                ) : null}
                {player.carryingFlagTeam ? (
                  <circle cx={pos.x} cy={pos.y - 8} r={2.5} fill={teamColor(player.carryingFlagTeam)} stroke="#fff" strokeWidth={0.6} />
                ) : null}
              </g>
            );
          })}
        </svg>

        {/* Header */}
        <div style={{
          position: 'absolute',
          top: 8,
          left: 12,
          fontSize: 11,
          letterSpacing: 2.2,
          textTransform: 'uppercase',
          color: '#62ddff',
          fontFamily: 'monospace',
        }}>
          Command Map
        </div>
        <div style={{
          position: 'absolute',
          top: 8,
          right: 12,
          fontSize: 9,
          letterSpacing: 1.6,
          textTransform: 'uppercase',
          color: 'rgba(231,244,252,0.55)',
          fontFamily: 'monospace',
        }}>
          M to close · {MAP_RADIUS * 2}u scale
        </div>
      </div>
    </div>
  );
}
